import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect
} from 'react'
import * as d3 from 'd3'
import useSize from 'hooks/useSize'
import browsercheck from '@ta-interaktiv/browsercheck'
import { useSpring, animated, config } from '@react-spring/web'
import { Municipality } from 'types/municipalityChart'
import './styles.scss'
import { formatFortuneSmallSteps } from 'components/misc'
import Worker from './chart.worker.js'

const isMobile = window.innerWidth < 900

interface Props {
  data: Array<Municipality>
  selectedMunicipalityNumber: number
}

export const MunicipalityChart = ({
  data,
  selectedMunicipalityNumber
}: Props) => {
  const chartWorker = useRef<Worker | null>(null)
  const [container, size] = useSize()
  const [showLoading, setShowLoading] = useState(true)
  const [selectedMunicipality, setSelectedMunicipality] = useState<
    (Municipality & { vx: number; vy: number; x: number; y: number }) | null
  >(null)
  const [circles, setCircles] = useState(null)
  const heightRatio = isMobile ? 2.5 : 0.5
  const loadingBarNode = useRef<HTMLDivElement | null>(null)
  const width = size.width
  const height = size.width * heightRatio
  const padding = 6
  const gutterTop = isMobile ? 90 : padding
  const gutterRight = padding
  const gutterLeft = isMobile ? 6 : 100
  const gutterBottom = 50
  const plotArea = {
    top: gutterTop,
    right: width - gutterRight,
    left: gutterLeft,
    bottom: height - gutterBottom
  }
  const [showInfos, setShowInfos] = useState(null)
  const hidePopupTimeout = useRef(null)
  const [popupStyles, popupApi] = useSpring(() => ({
    translateY: 0,
    translateX: 0,
    config: config.wobbly
  }))

  const radius = isMobile ? 10 : 10
  const spaceBetween = isMobile ? 3 : 3

  // When the worker is building the simulation
  // on each tick, make the loading bar wider
  const ticked = (data: { type: string; progress: number }) => {
    const progress = data.progress
    if (loadingBarNode && loadingBarNode.current) {
      loadingBarNode.current.style.width = 100 * progress + '%'
    }
  }

  const x = d3
    .scaleLinear()
    .range([0, width - gutterLeft - gutterRight])
    .domain([0, 40000])

  const y = d3
    .scaleLinear()
    .range([height - gutterTop - gutterBottom, 0])
    .domain([0, 40000])

  // When the simulation has ended building the graph
  const ended = (data: {
    type: string
    nodes: Array<
      Municipality & { vx: number; vy: number; x: number; y: number }
    >
  }) => {
    // Remove the loading bar
    setShowLoading(false)
    console.log(data.nodes)

    setCircles(
      data.nodes.map(node => ({
        fill: node.image ? `url(#${node.id})` : '#ffffff',
        bck: node.image
          ? `url(${require(`../../images/portraits/${node.id}.jpg`)})`
          : '#ffffff',
        cx: node.x,
        cy: node.y,
        r:
          (isMobile ? 5 : 7) +
          (radius * node.fortune) / (isMobile ? 10000 : 9000) -
          spaceBetween,
        name: node.name,
        fortune: formatFortuneSmallSteps(node.fortune)
      }))
    )
  }

  const init = () => {
    setShowLoading(true)
    // https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Using_web_workers
    // https://webpack.js.org/loaders/worker-loader/
    // https://stackoverflow.com/questions/50778655/how-to-use-web-workers-in-create-react-app-with-worker-loader
    if (chartWorker && chartWorker.current) {
      console.log('Terminate worker')
      chartWorker.current.terminate()
    }

    chartWorker.current = new Worker()

    console.log('NEW WORKER')

    chartWorker.current.postMessage({
      nodes: data,
      dimensions: {
        width: width,
        height: height
      },
      margins: {
        top: gutterTop,
        left: gutterLeft,
        bottom: gutterBottom,
        right: gutterRight
      },
      radius: radius,
      isMobile: isMobile
    })

    chartWorker.current.onmessage = (event: MessageEvent) => {
      switch (event.data.type) {
        case 'tick':
          return ticked(event.data)
        case 'end':
          return ended(event.data)
      }
    }

    console.log('INITIALIZED')
  }

  const loadingBar = useCallback(node => {
    loadingBarNode.current = node
  }, [])

  // Redraw when the dimensions have changed
  useLayoutEffect(() => {
    if (width > 0) {
      init()
    }
  }, [size])

  const ticks = [1000, 9000, 18000, 31000]
  const tickBars = [
    0,
    1000,
    2000,
    3000,
    4000,
    5000,
    6000,
    7000,
    8000,
    9000,
    10000,
    11000,
    12000,
    13000,
    14000,
    15000,
    16000,
    17000,
    18000,
    19000,
    20000,
    21000,
    22000,
    23000,
    24000,
    25000,
    26000,
    27000,
    28000,
    29000,
    30000,
    31000,
    32000,
    33000,
    34000
  ]

  return (
    <div className='beeswarm-container' ref={container}>
      {showLoading && (
        <div className='loading-screen'>
          <div className='loading-bar' ref={loadingBar} />
        </div>
      )}

      {showInfos && (
        <animated.div className='circle-popup' style={popupStyles}>
          <div>
            <b>{showInfos.name}</b>
          </div>
          <div>{showInfos.fortune}</div>
        </animated.div>
      )}

      {circles ? (
        <div style={{ position: 'relative', width: width, height: height }}>
          <div
            style={{
              position: 'relative',
              height: '100%',
              width: `${plotArea.right - plotArea.left}px`,
              transform: `translate(${gutterLeft}px, ${gutterTop}px)`
            }}
          >
            <div
              className='selected-profile-rank-label right'
              style={{
                transform: `translate(${circles[0].cx}px,${circles[0].cy -
                  circles[0].r -
                  120}px)`
              }}
            >
              <div>
                <div>
                  <b>{circles[0].name}</b>
                </div>
                <div>{circles[0].fortune}</div>
              </div>
              <svg
                version='1.1'
                xmlns='http://www.w3.org/2000/svg'
                xmlnsXlink='http://www.w3.org/1999/xlink'
                x='0px'
                y='0px'
                viewBox='0 0 20.5 17.6'
                xmlSpace='preserve'
              >
                <path
                  d='M2.1,1.1c2.2,0,7.6,0.3,11.4,3.8c2.6,2.4,3.9,5.9,3.9,10.2l1.4-1.5l0.4,0.3l-2,2.2L15,14l0.4-0.4l1.5,1.5
	c0-4.3-1.2-7.6-3.7-10c-3.6-3.4-8.9-4-11-4'
                />
              </svg>
            </div>

            {tickBars.map((t, i) => (
              <div key={`tick-${i}`}>
                <div
                  style={{
                    transform: isMobile
                      ? `translateY(${y(t)}px)`
                      : `translateX(${x(t)}px)`,
                    height: isMobile ? 0 : plotArea.bottom - plotArea.top - 35,
                    width: isMobile ? plotArea.right - plotArea.left : 0,
                    bottom: isMobile ? 'auto' : gutterBottom
                  }}
                  className={`tick min ${isMobile ? 'horizontal' : 'vertical'}`}
                />
              </div>
            ))}

            {ticks.map((t, i) => (
              <div key={`tick-${i}`}>
                <div
                  style={{
                    transform: isMobile
                      ? `translateY(${y(t)}px)`
                      : `translateX(${x(t)}px)`,
                    height: isMobile ? 0 : plotArea.bottom - plotArea.top - 35,
                    width: isMobile ? plotArea.right - plotArea.left : 0,
                    bottom: isMobile ? 'auto' : gutterBottom
                  }}
                  className={`tick ${isMobile ? 'horizontal' : 'vertical'}`}
                />
              </div>
            ))}

            {circles.map((p, i) => (
              <div
                onMouseOut={e => {
                  clearTimeout(hidePopupTimeout.current)
                  hidePopupTimeout.current = setTimeout(() => {
                    setShowInfos(null)
                  }, 1000)
                }}
                onMouseOver={e => {
                  setShowInfos(p)
                  clearTimeout(hidePopupTimeout.current)

                  const px =
                    e.target.getBoundingClientRect().x -
                    container.current.getBoundingClientRect().x
                  const py =
                    e.target.getBoundingClientRect().y -
                    container.current.getBoundingClientRect().y

                  const tx =
                    px < container.current.getBoundingClientRect().width / 2
                      ? px
                      : px - 200
                  const ty =
                    py < container.current.getBoundingClientRect().height / 2
                      ? py + 45
                      : py - 90

                  popupApi.start({
                    translateX: tx,
                    translateY: ty
                  })
                }}
                className='circle-test-outline'
                key={`circle-${i}`}
                style={{
                  height: p.r * 2,
                  width: p.r * 2,
                  transform: `translate(${p.cx - p.r}px, ${p.cy - p.r}px)`
                }}
              >
                <div
                  className='circle-test'
                  style={{
                    backgroundImage: p.r > (isMobile ? 7 : 9) ? p.bck : 'none'
                  }}
                />
              </div>
            ))}

            {ticks.map((t, i) => (
              <div key={`tick-${i}`}>
                <div
                  key={`label-${i}`}
                  style={{
                    transform: isMobile
                      ? `translateY(${y(t) - 15}px)`
                      : `translateX(${x(t)}px)`,
                    height: isMobile ? 0 : plotArea.bottom - plotArea.top - 35,
                    top: isMobile ? 'auto' : plotArea.top
                  }}
                  className='label'
                >
                  <span>
                    {i < 3
                      ? formatFortuneSmallSteps(t).replace(' milliards', '')
                      : formatFortuneSmallSteps(t)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className='ui active inverted dimmer'>
          <div className='ui text loader'>Chargement</div>
        </div>
      )}
    </div>
  )
}
